<template>
  <div class="saml-callback-container">
    <div class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3">Verificando autenticación...</p>
    </div>
  </div>
</template>

<script>
import { authHandler } from '@/auth/mixins/auth-handler'

export default {
  name: 'SAMLCallback',

  mixins: [authHandler],

  inject: {
    authApiUrl: {
      default: process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com/auth'
    }
  },

  data() {
    return {
      error: null
    }
  },

  computed: {
    communitySlug() {
      return this.$route.params.communityId;
    }
  },

  async created() {
    await this.verifyAuthentication();
  },

  methods: {
    async verifyAuthentication() {
      try {
        console.log('SAMLCallback: Verifying authentication from httpOnly cookies...');
        
        // The backend should have set httpOnly cookies (frontendToken, backendToken)
        // We need to verify these cookies by making a request to an endpoint that reads them
        // Option 1: Call an endpoint that verifies SAML authentication and returns token/user info
        // Option 2: Call the login endpoint which should recognize the cookies
        
        const baseUrl = process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com';
        
        // The backend has set httpOnly cookies (frontendToken, backendToken)
        // We need to verify these cookies and get a frontend-accessible token
        // The backend should read the httpOnly cookies and return:
        // - The token value (so frontend can set it in a non-httpOnly cookie for API calls)
        // - User info
        // - Session info
        const verifyUrl = `${baseUrl}/v1/auth/verify-saml-session`;
        
        console.log('SAMLCallback: Calling verify endpoint:', verifyUrl);
        console.log('SAMLCallback: httpOnly cookies (frontendToken, backendToken) should be sent automatically');
        
        const response = await fetch(verifyUrl, {
          method: 'POST',
          credentials: 'include', // Include cookies (httpOnly cookies will be sent automatically)
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            communitySlug: this.communitySlug
          })
        });

        console.log('SAMLCallback: Response status:', response.status);

        if (!response.ok) {
          // If 401, cookies are not valid or not present
          if (response.status === 401) {
            throw new Error('Authentication failed - httpOnly cookies not found or invalid');
          }
          throw new Error(`Verification failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('SAMLCallback: Verification response:', data);

        // The backend should return either:
        // 1. A token to set in a non-httpOnly cookie for frontend use (for API calls)
        // 2. User info indicating authentication is complete
        // 3. Session info with token
        
        if (data.token) {
          console.log('SAMLCallback: Token received, setting frontend cookie...');
          // Set the token in a non-httpOnly cookie for frontend JavaScript access
          // This allows the frontend to make API calls with Authorization header
          await this.handleAuthSuccess({ token: data.token });
        } else if (data.frontendToken) {
          console.log('SAMLCallback: Frontend token received, setting cookie...');
          // Backend provided a frontend-accessible token
          await this.handleAuthSuccess({ token: data.frontendToken });
        } else if (data.sessionInfo) {
          console.log('SAMLCallback: Session info received...');
          // Handle session info format (similar to normal login)
          await this.handleAuthSuccess(data);
        } else if (data.authenticated || data.user) {
          // If already authenticated via httpOnly cookies but no token provided
          // The httpOnly cookies (frontendToken, backendToken) are already set
          // We need to extract the token from cookies or get it from backend
          console.log('SAMLCallback: Authenticated via httpOnly cookies, but no token in response');
          console.log('SAMLCallback: Attempting to get token from backend...');
          
          // Try to get the token by calling an endpoint that reads the httpOnly cookies
          // and returns a frontend-accessible token
          try {
            const tokenUrl = `${baseUrl}/v1/auth/get-frontend-token`;
            const tokenResponse = await fetch(tokenUrl, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              if (tokenData.token) {
                console.log('SAMLCallback: Got frontend token from backend');
                await this.handleAuthSuccess({ token: tokenData.token });
                return;
              }
            }
          } catch (tokenError) {
            console.warn('SAMLCallback: Could not get frontend token:', tokenError);
          }
          
          // If we can't get a token, the httpOnly cookies should work for API calls
          // But we still need a frontend-accessible token for JavaScript
          // For now, redirect and let the auth guard handle it
          console.log('SAMLCallback: httpOnly cookies set, redirecting to main app...');
          console.warn('SAMLCallback: Note - frontend may need a non-httpOnly token for API calls');
          await this.handleRedirect();
        } else {
          throw new Error('Authentication verification failed - invalid response');
        }
      } catch (error) {
        console.error('SAMLCallback: Error verifying authentication:', error);
        this.error = error.message || 'Error de autenticación';
        
        // Redirect to login page on error
        setTimeout(() => {
          this.$router.push({
            name: 'auth-login',
            params: { communityId: this.communitySlug }
          });
        }, 2000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.saml-callback-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>

